"use client";

import { useState, useMemo } from "react";
import { Plus, Check, Loader2, AlertCircle } from "lucide-react";
import { MainShopProduct } from "@/core/models/shop-product-model";
import { useCartStore } from "@/core/hooks/cart/useCartStore";
import { useAuth } from "@/contexts/authContext";
import CustomButton from "./CustomButton";

interface AddAllToCartProps {
    products: MainShopProduct[];
    className?: string;
}

export function AddAllToCart({ products, className }: AddAllToCartProps) {
    const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
    const { addToCart, isCartLoading } = useCartStore();
    const {
        state: { isAuthenticated },
    } = useAuth();

    // Filter products that are in stock
    const { inStockProducts, outOfStockCount } = useMemo(() => {
        const inStock = products.filter(
            (product) => product.inventory && product.inventory > 0
        );
        return {
            inStockProducts: inStock,
            outOfStockCount: products.length - inStock.length,
        };
    }, [products]);

    const handleAddAll = async () => {
        if (inStockProducts.length === 0) return;

        setStatus("loading");

        try {
            for (const product of inStockProducts) {
                await addToCart(product, 1, isAuthenticated);
            }

            setStatus("success");
            setTimeout(() => setStatus("idle"), 2500);
        } catch (error) {
            console.error("Error adding products to cart:", error);
            setStatus("idle");
        }
    };

    const isDisabled =
        status === "loading" ||
        status === "success" ||
        isCartLoading ||
        inStockProducts.length === 0;

    const totalPrice = inStockProducts.reduce(
        (sum, p) => sum + (p.price_withDiscount || p.price || 0),
        0
    );

    return (
        <div
            className={`flex flex-col gap-3 p-5 border border-neutral-200 bg-neutral-50 ${className}`}
        >
            <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col">
                    <span className="text-xs text-neutral-500 tracking-wide">
                        {inStockProducts.length} محصول موجود
                    </span>
                    <span className="text-base font-medium text-neutral-900 mt-0.5">
                        {totalPrice.toLocaleString("fa-IR")} تومان
                    </span>
                </div>

                <CustomButton
                    onClick={handleAddAll}
                    disabled={isDisabled}
                    variant="secondary"
                    className="flex items-center justify-center gap-2"
                >
                    {status === "loading" || isCartLoading ? (
                        <>
                            <Loader2 size={18} className="animate-spin" />
                            <span>در حال افزودن</span>
                        </>
                    ) : status === "success" ? (
                        <>
                            <Check size={18} />
                            <span>اضافه شد</span>
                        </>
                    ) : (
                        <>
                            <Plus size={18} />
                            <span>افزودن همه به سبد</span>
                        </>
                    )}
                </CustomButton>
            </div>

            {outOfStockCount > 0 && (
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                    <AlertCircle size={14} />
                    <span>
                        {outOfStockCount} محصول ناموجود و غیرقابل افزودن به سبد
                    </span>
                </div>
            )}
        </div>
    );
}

// Mobile version - Fixed bottom bar
export function AddAllToCartMobile({ products, className }: AddAllToCartProps) {
    const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
    const { addToCart, isCartLoading } = useCartStore();
    const {
        state: { isAuthenticated },
    } = useAuth();

    // Filter products that are in stock
    const { inStockProducts, outOfStockCount } = useMemo(() => {
        const inStock = products.filter(
            (product) => product.inventory && product.inventory > 0
        );
        return {
            inStockProducts: inStock,
            outOfStockCount: products.length - inStock.length,
        };
    }, [products]);

    const handleAddAll = async () => {
        if (inStockProducts.length === 0) return;

        setStatus("loading");

        try {
            for (const product of inStockProducts) {
                await addToCart(product, 1, isAuthenticated);
            }

            setStatus("success");
            setTimeout(() => setStatus("idle"), 2500);
        } catch (error) {
            console.error("Error adding products to cart:", error);
            setStatus("idle");
        }
    };

    const isDisabled =
        status === "loading" ||
        status === "success" ||
        isCartLoading ||
        inStockProducts.length === 0;

    const totalPrice = inStockProducts.reduce(
        (sum, p) => sum + (p.price_withDiscount || p.price || 0),
        0
    );

    return (
        <div className={`fixed bottom-0 left-0 right-0 md:hidden bg-white border-t border-neutral-200 p-4 z-50 ${className}`}>
            <div className="flex flex-col gap-2">
                {outOfStockCount > 0 && (
                    <div className="flex items-center gap-2 text-xs text-neutral-500">
                        <AlertCircle size={14} />
                        <span>{outOfStockCount} محصول ناموجود</span>
                    </div>
                )}
                <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-col">
                        <span className="text-xs text-neutral-500">
                            {inStockProducts.length} محصول موجود
                        </span>
                        <span className="text-sm font-medium text-neutral-900">
                            {totalPrice.toLocaleString("fa-IR")} تومان
                        </span>
                    </div>
                    <CustomButton
                        onClick={handleAddAll}
                        disabled={isDisabled}
                        variant="secondary"
                        className="flex-1 flex items-center justify-center gap-2"
                    >
                        {status === "loading" || isCartLoading ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                <span>در حال افزودن</span>
                            </>
                        ) : status === "success" ? (
                            <>
                                <Check size={18} />
                                <span>اضافه شد</span>
                            </>
                        ) : (
                            <>
                                <Plus size={18} />
                                <span>افزودن همه به سبد</span>
                            </>
                        )}
                    </CustomButton>
                </div>
            </div>
        </div>
    );
}
